import React from 'react';
import { Box, LinearProgress, Typography, keyframes } from '@mui/material';
import { DirectionsRun as RunIcon } from '@mui/icons-material';

interface AppLoadingBarProps {
  progress: number; // 0-100
  message?: string;
}

// Pulse animation for the runner icon (vertical bounce to simulate running)
const pulseAnimation = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
`;

const AppLoadingBar: React.FC<AppLoadingBarProps> = ({ progress, message }) => {
  // Calculate runner position based on progress (clamp between 0 and 100)
  const clampedProgress = Math.min(Math.max(progress, 0), 100);
  const runnerPosition = `${clampedProgress}%`;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        backgroundColor: 'white',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      }}
    >
      {/* Progress Bar Container */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '6px',
          overflow: 'hidden',
        }}
      >
        {/* Background Progress Bar */}
        <LinearProgress
          variant="determinate"
          value={clampedProgress}
          sx={{
            height: '6px',
            backgroundColor: '#e0e0e0',
            '& .MuiLinearProgress-bar': {
              backgroundColor: '#1976d2',
              transition: 'transform 0.3s ease-out',
            },
          }}
        />

        {/* Runner Icon - positioned along the progress bar */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: runnerPosition,
            transform: 'translate(-50%, -50%)',
            zIndex: 1,
            transition: 'left 0.3s ease-out',
          }}
        >
          <Box
            sx={{
              animation: `${pulseAnimation} 0.8s ease-in-out infinite`,
            }}
          >
            <RunIcon
              sx={{
                fontSize: 24,
                color: '#1976d2',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Progress Percentage and Message */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          backgroundColor: 'white',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontSize: '0.875rem',
            fontWeight: 500,
          }}
        >
          {message || 'Loading Waka Running...'}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'primary.main',
            fontSize: '0.875rem',
            fontWeight: 600,
            minWidth: '45px',
            textAlign: 'right',
          }}
        >
          {Math.round(progress)}%
        </Typography>
      </Box>
    </Box>
  );
};

export default AppLoadingBar;

